import * as Sentry from "@sentry/react";
import React from "react";
import { createRoot } from "react-dom/client";
import LoginWrapper from "./vb-django/components/login/LoginWrapper";
import { SubscriptionCheckoutWrapper } from "@components/Checkout/config/SubscriptionCheckout/SubscriptionCheckoutContext";
import tailwindCSS from "./styles/tailwind.css";
import { setAbTestCookies } from "./utils/abTestCookies.js";

let sentryInitialized = false;
let polyfillsInitialized = false;
let tailwindInjected = false;

export function injectTailwindCSS() {
  if (tailwindInjected) return;
  if (!document.querySelector('style[data-tailwind="global"]')) {
    const style = document.createElement('style');
    style.setAttribute('data-tailwind', 'global');
    style.textContent = tailwindCSS;
    document.head.appendChild(style);
  }
  tailwindInjected = true;
}

export function initSentry() {
  if (sentryInitialized) return;
  if (process.env.NODE_ENV === "development") {
    sentryInitialized = true;
    return;
  }

  const isProduction = process.env.NODE_ENV === "production";

  Sentry.init({
    dsn: "https://ab3db9a4c5d5dbd88981ecbe7ee71e91@o4508850731614208.ingest.de.sentry.io/4508850783584336",
    integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
    release: process.env.SENTRY_RELEASE,
    environment: process.env.SENTRY_ENVIRONMENT || process.env.NODE_ENV || "production",
    beforeSend: (event) => {
      const userAgent = event.request?.headers?.["User-Agent"];
      const ignoredUserAgents = ["YisouSpider", "HeadlessChrome"];
      if (userAgent && ignoredUserAgents.some((agent) => userAgent.includes(agent))) {
        return null;
      }

      const url = event.request?.url;
      if (url && (url.includes("/vb-admin/") || url.match(/\/vb-admin($|\/).*/))) {
        return null;
      }

      return event;
    },
    tracesSampler: ({ name }) => {
      if (name?.includes("error") || name?.includes("exception")) {
        return isProduction ? 0.6 : 0.1;
      }
      return isProduction ? 0.2 : 0.1;
    },
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: isProduction ? 0.3 : 0.1,
  });

  sentryInitialized = true;
}

export function initPolyfills() {
  if (polyfillsInitialized) return;

  require("es6-promise").polyfill();
  require("isomorphic-fetch");
  require("./polyfills.js");

  polyfillsInitialized = true;
}

export function initializeReactApp(config) {
  const {
    componentName,
    componentFactory,
    skipComponents = []
  } = config;

  initSentry();
  initPolyfills();
  injectTailwindCSS();
  setAbTestCookies();

  function mountComponents() {
    const selector = componentName
      ? `div[data-type="react"][data-component="${componentName}"]`
      : 'div[data-type="react"]';

    const elems = document.querySelectorAll(selector);

    elems.forEach((elem) => {
      const name = elem.getAttribute("data-component");

      if (skipComponents.includes(name)) {
        return;
      }

      const Comp = componentFactory(name);
      if (!Comp) {
        return;
      }

      const props = JSON.parse(elem.getAttribute("data-component_props") || "{}");
      const root = createRoot(elem);
      root.render(React.createElement(Comp, props));
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", mountComponents);
  } else {
    mountComponents();
  }
}

export function withSentryUserData(WrappedComponent) {
  return class extends React.Component {
    componentDidMount() {
      if (Window?.user_data?.isLogged) {
        let userTag = "authenticated";
        if (Window.user_data.isSuperUser || Window.user_data.isAdmin || Window.user_data.isExternalAdmin) {
          userTag = "admin";
        }
        Sentry.setTag("user_type", userTag);

        let role = "user";
        if (Window.user_data.isSuperUser) role = "super_admin";
        else if (Window.user_data.isAdmin) role = Window.user_data.isExternalAdmin ? "external_admin" : "internal_admin";

        Sentry.setUser({
          accountId: Window.user_data.loggedInUserID.toString(),
          permission_role: role,
        });
      } else {
        Sentry.setTag("user_type", "anonymous");
        Sentry.setUser(null);
      }
    }

    render() {
      return <WrappedComponent {...this.props} />;
    }
  };
}

export function wrapWithLoginPopup(Component) {
  return (props) => (
    <LoginWrapper>
      <Component {...props} />
    </LoginWrapper>
  );
}

export function wrapWithSubscriptionCheckout(Component) {
  return (props) => (
    <SubscriptionCheckoutWrapper>
      <Component {...props} />
    </SubscriptionCheckoutWrapper>
  );
}
